import {
  Button,
  Card,
  CardHeader,
  withStyles,
  WithStyles,
} from "@material-ui/core";
import { CloudDownload as CloudDownloadIcon } from "@material-ui/icons";
import DeleteIcon from '@material-ui/icons/Delete';
import { format, getTime } from "date-fns";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TableCellProps } from "react-virtualized";

import { Table, sortTableWithKey } from "./../../common";
import {
  actionDownloadReport,
  actionDeleteReport,
  actionFetchReports,
  ReportType,
} from "./actions";
import { AddLabattReport as AddLabattReportDialog } from "./Dialogs";
import { LabattReportsTableColumns } from "./helpers";
import styles from "./styles";

interface LabattReportDashboard extends WithStyles<typeof styles> {}

function LabattReportDashboard({ classes }: LabattReportDashboard) {
  const [showLabattReportModal, setAddLabattReportModal] = useState(false);
  const dispatch = useDispatch();

  const labattReportsTableColumns = useRef(LabattReportsTableColumns);

  useEffect(() => {
    dispatch(actionFetchReports(ReportType.Labatt));
  }, []);

  const isFetchingReports = useSelector(
    (state: any) => state.reports.isFetchingReports
  );

  const reports = useSelector((state) =>
    // @ts-ignore
    state.reports.data.filter(
      // @ts-ignore
      (report) => report.get("type") === ReportType.Labatt
    )
  );

  const { sortedList: sortedReports } = sortTableWithKey(
    reports,
    "receivedAt",
    "desc",
    (row, orderByKey) => getTime(new Date(row.get(orderByKey)))
  );

  /**
   *
   */
  const _memoizedRowGetter = useCallback(
    ({ index }) => sortedReports.get(index),
    [sortedReports]
  );

  /**
   *
   */
  const determineCellContent = ({
    cellData,
    dataKey,
    rowData,
  }: TableCellProps): {
    cellContent: JSX.Element | string;
    cellClass?: string;
  } => {
    let cellContent: JSX.Element | string = "";
    let cellClass;

    const rowId = rowData.get("id");
    const rowNumber = rowData.get("numberId");

    if (dataKey === "number") {
      cellContent = <span>{rowNumber}</span>;
    }

    if (dataKey === "startRange") {
      cellContent = format(new Date(cellData), "MMM dd, yyyy, h:mm aaaa");
    }

    if (dataKey === "endRange") {
      cellContent = format(new Date(cellData), "MMM dd, yyyy, h:mm aaaa");
    }

    if (dataKey === "download") {
      cellContent = (
        <Button
          size="small"
          disableElevation
          variant="contained"
          startIcon={<CloudDownloadIcon />}
          onClick={() =>
            dispatch(actionDownloadReport(ReportType.Labatt, rowId))
          }
        >
          Download
        </Button>
      );

      cellClass = classes.blockTableCell;
    }

    if (dataKey === "delete") {
      cellContent = (
        <Button
          size="small"
          disableElevation
          variant="contained"
          startIcon={<DeleteIcon />}
          onClick={() =>
            dispatch(actionDeleteReport(ReportType.Labatt, rowId))
          }
        >
          Delete
        </Button>
      );
      cellClass = classes.blockTableCell;
    }

    return {
      cellContent,
      cellClass,
    };
  };

  return (
    <>
      <Card square elevation={0} className={classes.header}>
        <CardHeader
          action={
            <>
              <Button
                color="primary"
                variant="contained"
                onClick={() => setAddLabattReportModal(true)}
              >
                Add a labatt report
              </Button>
            </>
          }
        />
      </Card>

      <Card square className={classes.orderLabattReportCard}>
        <CardHeader
          subheader="Order Labatt reports"
          className={classes.orderLabattReportCardHeader}
        />

        <Table
          renderEmptyHeader
          tableRowHeight={65}
          rowCount={reports.size}
          tableHeaderRowHeight={50}
          rowGetter={_memoizedRowGetter}
          columns={labattReportsTableColumns.current}
          determineCellContent={determineCellContent}
        />
      </Card>

      <AddLabattReportDialog
        show={showLabattReportModal}
        onClose={() => setAddLabattReportModal(false)}
      />
    </>
  );
}

export default withStyles(styles)(LabattReportDashboard);
